import { useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Badge, IconButton } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { useLocalStorage } from "app/pages/activation/hooks/helpers/useLocalStorage";
import { EXTENSION_LINK } from "app/pages/sales-intelligence/constants/common";
import downloadExtensionTrackingService from "app/pages/sales-intelligence/services/tracking/download-extension/trackingService";
import { AssetsService } from "app/services/AssetsService";
import { isTraditionalTrialUser } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import {
    StyledDownloadExtensionBannerContainer,
    StyledDownloadExtensionContent,
    StyledCloseButton,
    StyledDownloadExtensionImage,
    StyledDownloadExtensionLink,
    StyledDownloadExtensionTitle,
} from "./styles";

const SI_DOWNLOAD_EXTENSION_BANNER_DISMISSED_KEY = "si.download-extension-banner.dismissed";

export const DownloadExtensionBanner = () => {
    const { translate } = useTranslate();
    const { getLocalStorage, setLocalStorage } = useLocalStorage();

    const initialIsDismissed = useMemo(
        () => Boolean(getLocalStorage<boolean>(SI_DOWNLOAD_EXTENSION_BANNER_DISMISSED_KEY)),
        [getLocalStorage],
    );
    const [isDismissed, setIsDismissed] = useState<boolean>(initialIsDismissed);

    const onClickExtensionTracking = () => downloadExtensionTrackingService.trackDownloadExtensionAction("home page");
    const handleClose = (): void => {
        setLocalStorage(SI_DOWNLOAD_EXTENSION_BANNER_DISMISSED_KEY, true);
        setIsDismissed(true);
    };

    if (isDismissed) {
        return null;
    }

    return (
        <StyledDownloadExtensionBannerContainer data-automation="si-download-extension-banner">
            {!isTraditionalTrialUser(swSettings) ? (
                <StyledCloseButton
                    type="flat"
                    iconName="clear"
                    iconSize="16px"
                    onClick={handleClose}
                    data-automation="si-download-extension-banner-close"
                />
            ) : null}
            <StyledDownloadExtensionContent>
                <StyledDownloadExtensionTitle>
                    <Text fontVariant="h5_2">{translate("si.homepage.extension.banner.title")}</Text>
                    <Badge type="new" />
                </StyledDownloadExtensionTitle>
                <Text fontVariant="body2">{translate("si.homepage.extension.description")}</Text>
                <StyledDownloadExtensionLink
                    target="_blank"
                    href={EXTENSION_LINK}
                    rel="noreferrer"
                    onClick={onClickExtensionTracking}
                >
                    <IconButton
                        type="primary"
                        iconSize="12px"
                        iconName="open-new-window"
                        placement="right"
                        data-automation="si-download-extension-banner-download"
                    >
                        {translate("si.common.download-extension")}
                    </IconButton>
                </StyledDownloadExtensionLink>
            </StyledDownloadExtensionContent>
            <Flex>
                <StyledDownloadExtensionImage
                    src={AssetsService.assetUrl(`/images/sales-intelligence/extension_demo_banner.svg`)}
                    alt="Download extension"
                />
            </Flex>
        </StyledDownloadExtensionBannerContainer>
    );
};
